<template>
  <section class="survey-results-chart mb-4">
    <div
      class="survey-results mb-4"
      id="survey-results mb-8"
      v-if="results.length"
      ref="surveyResults"
    >
      <div
        class="survey-results__legend d-md-flex justify-center"
        v-if="!isTheRiseFrameworkSurvey"
      >
        <div class="survey-results__legend-item mr-md-8">
          <span
            class="survey-results__legend-item-box section-summary-color--good"
          ></span>
          <strong class="section-summary-color--text-good">Doing well</strong>
        </div>
        <div class="survey-results__legend-item mr-md-8">
          <span
            class="survey-results__legend-item-box section-summary-color--normal"
          ></span>
          <strong class="section-summary-color--text-normal"
            >Could improve</strong
          >
        </div>
        <div class="survey-results__legend-item">
          <span
            class="survey-results__legend-item-box section-summary-color--bad"
          ></span>
          <strong class="section-summary-color--text-bad"
            >Needs improvement</strong
          >
        </div>
      </div>
      <div
        :class="
          `survey-results__chart-holder ${
            surveyResultCanScrollLeft ? 'allow-scroll-left ' : ''
          } ${surveyResultCanScrollRight ? 'allow-scroll-right ' : ''}`
        "
      >
        <div
          class="survey-results__chart"
          :v-on-load="startObeserveScroll"
          ref="surveyResultsChart"
        >
          <v-tooltip
            v-for="(result, index) in results"
            :key="stringify(result.title)"
            :title="result.title"
            bottom
          >
            <template v-slot:activator="{ on, attrs }">
              <div class="survey-results__chart-item" v-bind="attrs" v-on="on">
                <span
                  :class="
                    `${
                      isTheRiseFrameworkSurvey ? '' : 'bhGray6'
                    } survey-results__chart-item-bar`
                  "
                >
                  <span
                    :class="
                      `survey-results__chart-item-bar-fill section-summary-color--${getSummaryStatus(
                        result.summary,
                      )}`
                    "
                    :style="
                      `height: ${(result.score / result.maxScore) * 100}%;`
                    "
                  ></span>
                </span>
                <div class="survey-results__chart-item-bar-footer">
                  <v-img
                    class="mx-1 survey-results__chart-item-bar-footer-icon"
                    v-if="icons[index]"
                    :src="icons[index]"
                    :alt="result.title"
                  />
                </div>
              </div>
            </template>
            <span class="d-inline-block text-center"
              >{{ result.title }}<br />
              <template v-if="isTheRiseFrameworkSurvey">Mind Trap</template
              ><template v-else
                >{{ result.score }} / {{ result.maxScore }}</template
              ></span
            >
          </v-tooltip>
        </div>
      </div>
      <div class="survey-results__total-score" v-if="!isTheRiseFrameworkSurvey">
        <div class="survey-results__total-score-progress mt-4 mx-4">
          <v-progress-circular
            :rotate="-90"
            :size="100"
            :width="8"
            :value="totalScorePercentage"
            color="accent"
          >
            <span class="survey-results__total-score-value">{{
              totalScore
            }}</span>
          </v-progress-circular>
          <span class="survey-results__total-score-label">YOUR SCORE</span>
        </div>
        <template v-if="hasAdditionalScores">
          <div
            class="survey-results__total-score-progress mt-4 mx-4"
            v-for="(additionalScore, index) in additionalScores"
            :key="`additional-score-${index}-${additionalScore.title}`"
          >
            <v-progress-circular
              :rotate="-90"
              :size="100"
              :width="8"
              :value="100"
              color="accent"
            >
              <span class="survey-results__total-score-value">{{
                additionalScore.value
              }}</span>
            </v-progress-circular>
            <span class="survey-results__total-score-label">{{
              additionalScore.title
            }}</span>
          </div>
        </template>
      </div>
    </div>
    <div class="mb-4 survey-results-texts">
      <v-expansion-panels v-model="resultsPanels" multiple>
        <v-expansion-panel
          class="survey-results-texts__item"
          v-for="(result, index) in results"
          :key="`result-text-${result.title}`"
        >
          <v-expansion-panel-header class="survey-results-texts__item-header">
            <v-img
              v-if="icons[index]"
              :src="icons[index]"
              :alt="result.title"
            />
            <h2
              :class="
                `${isTheRiseFrameworkSurvey ? 'mainColor--text' : ''} mb-0 mt-0`
              "
            >
              {{ resultsTexts[index].title }}
            </h2>
            <h5
              :class="
                `d-none text-right d-sm-inline mb-0 mr-4 section-summary-color--text-${getSummaryStatus(
                  result.summary,
                )}`
              "
            >
              Read more
            </h5>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="survey-results-texts__item-content">
            <template v-if="isTheRiseFrameworkSurvey">
              <ul class="survey-results-texts__item-tips mb-6">
                <li
                  v-for="(tip, tipIndex) in resultsTexts[index].tips"
                  :key="`tip-${result.title}-${tipIndex}`"
                >
                  {{ tip }}
                </li>
              </ul>
              <div class="survey-results-texts__item-compare mb-4">
                <h4
                  class="survey-results-texts__item-compare-title survey-results-texts__item-compare-title--win"
                >
                  Win
                </h4>
                <p>{{ resultsTexts[index].win }}</p>
              </div>
              <div class="survey-results-texts__item-compare mb-6">
                <h4
                  class="survey-results-texts__item-compare-title survey-results-texts__item-compare-title--lose"
                >
                  Risk
                </h4>
                <p>{{ resultsTexts[index].lose }}</p>
              </div>
              <p class="survey-results-texts__item-example text-center">
                <i>{{ resultsTexts[index].example }}</i>
              </p>
              <p class="text-center">
                <a href="https://theriseframework.com/" target="_blank"
                  >Find more Insights on my website</a
                >
              </p>
            </template>
            <template v-else>
              <p v-html="resultsTexts[index][result.summary].text"></p>
              <p v-if="expandedTips[index] === false" class="text-center">
                <a
                  @click="
                    ev => {
                      ev.preventDefault()
                      showTips(index)
                    }
                  "
                  >{{ resultsTexts[index][result.summary].moreTips }}</a
                >
                or
                <a :href="resultsTexts[index].infoLink" target="_blank">{{
                  resultsTexts[index].moreInfo.toLowerCase()
                }}</a>
                <template v-if="resultsTexts[index].videoInfoLink">
                  or
                  <a
                    :href="resultsTexts[index].videoInfoLink"
                    target="_blank"
                    >{{ resultsTexts[index].moreVideoInfo.toLowerCase() }}</a
                  ></template
                >.
              </p>
              <template v-else>
                <p>
                  <strong>{{
                    resultsTexts[index][result.summary].tipsTitle
                  }}</strong>
                </p>
                <ul>
                  <li
                    v-for="(tip, tipIndex) in resultsTexts[index][
                      result.summary
                    ].tips"
                    :key="`tip-${tipIndex}-${tip.title}`"
                  >
                    <strong v-html="tip.title"></strong>&nbsp;<span
                      v-html="tip.description"
                    ></span>
                  </li>
                </ul>
                <p class="mt-4 text-center">
                  <a :href="resultsTexts[index].infoLink" target="_blank">{{
                    resultsTexts[index].moreInfo
                  }}</a>
                  <template v-if="resultsTexts[index].videoInfoLink">
                    or
                    <a
                      :href="resultsTexts[index].videoInfoLink"
                      target="_blank"
                      >{{ resultsTexts[index].moreVideoInfo.toLowerCase() }}</a
                    ></template
                  >.
                </p>
              </template>
            </template>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
    <div
      class="mt-8 mb-4 text-center book-promo"
      v-if="isTheRiseFrameworkSurvey"
    >
      <img
        :src="require('@/assets/the-rise-framework-cover-book.jpg')"
        alt="The Rise Framework - Doug Harrison with Victoria Sassine"
      />
      <div class="d-flex align-center justify-center flex-wrap">
        <v-btn
          href="https://a.co/d/0gdNRDRM"
          color="#00a987"
          rounded
          elevation="0"
          class="ma-4 book-promo__btn"
          target="_blank"
        >
          Buy The Rise Framework book
        </v-btn>
        <v-btn
          :disabled="loadingPrint"
          :loading="loadingPrint"
          @click="print"
          rounded
          color="primary"
          elevation="0"
          class="no-print ma-4"
        >
          Print or Save Results
        </v-btn>
      </div>
    </div>
    <div class="mb-4" v-else>
      <v-btn
        :disabled="loadingPrint"
        :loading="loadingPrint"
        @click="print"
        rounded
        color="primary"
        elevation="0"
        class="no-print mt-4"
      >
        Print or Save Results
      </v-btn>
    </div>
  </section>
</template>

<script>
import { stringify } from '@/helpers/stringify'
import { debounce } from '@/helpers/debounce'

let surveyResultIsScrolling = false

export default {
  name: 'SurveyResultsChart',
  props: {
    results: {
      type: Array,
      default: () => [],
    },
    isTheRiseFrameworkSurvey: {
      type: Boolean,
      default: false,
    },
    showAdditionalScores: {
      type: Boolean,
      default: false,
    },
    additionalScores: {
      type: [Array, null],
      default: () => [],
    },
  },
  data() {
    return {
      loadingPrint: false,
      surveyResultIsScrolling: false,
      surveyResultCanScrollLeft: false,
      surveyResultCanScrollRight: false,
      expandedTips: [...Array(10)].map(() => false),
      resultsPanels: [],
    }
  },
  computed: {
    hasAdditionalScores() {
      return this.showAdditionalScores && this.additionalScores?.length > 0
    },
    resultsTexts() {
      if (this.isTheRiseFrameworkSurvey) {
        return [
          {
            title: 'The Feature Lister',
            tips: [
              'Are very knowledgeable about the facts and functions built into their offering and possibly directly participated in product development.',
              'This knowledge can be very attractive to Prospects building confidence they are buying from an expert who really knows their stuff.',
              'Can become overly dependent on the feature list overpowering how life is improved with the offering and may overwhelm the prospect with details.',
            ],
            win:
              'Prospects who tend to be more technical and/or fact focused and grow confident in the expertise shared.',
            lose:
              'Prospects who feel overwhelmed by the list or who are looking for greater emotional inspiration.',
            example:
              'Andrea feels most comfortable listing all the great features included in her offering, but sometimes feels like the description can become overwhelming to her prospects and would like a more compelling way of presenting the story.',
          },
          {
            title: 'The Pillar Pitcher',
            tips: [
              'Has spent the most time building out the description of their offering and have developed 2-3 pillars (their three shiny apples) to pitch their proposition.',
              'Sales approach can be very successful with a well-defined strategy.',
              'Upside exists as messaging may not be adequately distinctive or might fail to address risk elements (buyers buy the basket of apples not just the shiny ones).',
            ],
            win: 'Prospects who like the clarity of the proposition.',
            lose:
              'Prospects who find the pillars are not supported by depth or recognize risk elements from what is stated or what they viewed as missing.',
            example:
              'Kim spent the time to figure out her core distinctions and they work well. Turns out there was even greater upside to nail a complete Rise Framework top to bottom and eliminate additional risk factors for her client prospects.',
          },
          {
            title: 'The Glorifier',
            tips: [
              'Loves the proposition they are representing and finds the use of robust adjectives as a wonderful means of presenting their offering.',
              'Done well, prospects can be very drawn to this style. The risk occurs for buyers who like simplicity or choose to be understated which can be common.',
              'Can create an “after picture” misaligned with the buyer perspective.',
            ],
            win: 'Prospects who are drawn to the vision being created.',
            lose:
              'Prospects who feel they are being oversold or who feel product details do not measure up to claims.',
            example:
              'Jim likes to describe the product as “The ultimate expression of exquisite luxury featuring the finest materials and craftsmanship incorporating innovative technology unparalleled in its class.”',
          },
          {
            title: 'The OverExplainer',
            tips: [
              'Are passionate, inspired, and deeply immersed into their propositions which can be very attractive to those they seek to inspire.',
              'Will often rely on anecdotal stories and experiences to convey their proposition looking for an emotional connection with their prospect.',
              'Often crave a means of conveying their proposition more clearly and succinctly.',
            ],
            win: 'Prospects who are drawn to your passion and conviction.',
            lose:
              'Prospects can become overwhelmed or may fear a lack of systems/people to deliver on good intentions.',
            example:
              'Joe attracts others because he is extremely passionate about what he does. But there are many times when he wishes there was a more efficient and clear-cut means of sharing what he provides to improve conversion to his cause.',
          },
          {
            title: 'The Day Jobber',
            tips: [
              'Is most comfortable doing what they do over selling what they do. They can be very successful if quality is so good that repeat customers also drive referrals.',
              'Often feel uncomfortable stating their value proposition when asked.',
              'May start overlooking flaws that emerge over time as markets shift or their product matures.',
            ],
            win:
              'Prospects who are drawn to the quality and reliability of the Day Jobber and their offering.',
            lose:
              'Prospects who never become aware of their offering or who receive a weak pitch when requested or when key customers are lost and are hard to replace.',
            example:
              'Bill’s company operates largely on autopilot, and they are in a fortunate position where they don’t need much, if any, marketing because their work is so strong. Bill has been caught in some downturns in the past when key clients were lost and were hard to replace and he often wishes he could be more comfortable with his elevator pitch.',
          },
          {
            title: 'The Tactician',
            tips: [
              'Are passionate about activating new ideas/tactics to grow and nurture their business which is essential for any business/person.',
              'Risk occurs when tactics are not built around a consistent North star or may not be fully baked for launch.',
              'This can cause a constant cycling of pursuing the next big idea.',
            ],
            win: 'Those Prospects drawn to the tactic.',
            lose:
              'Prospects who can see the tactic is not fully integrated into a deeper purpose for the business. Can lead to constant chasing of the right next thing.',
            example:
              'This month, the new marketing campaign is expected to be the new driving force. Last quarter it was this new piece of equipment. We keep activating tactics, but they don’t materialize in the way we had hoped so we move onto the next one.',
          },
          {
            title: 'The Defender',
            tips: [
              'Has a great deal of conviction in what he/she is bringing to the world with a strong commitment to sticking with the plan or historical approach.',
              'Everyone should be able to effectively defend their choices and actions.',
              'Risk emerges when defensiveness is too strong and opportunities for improvement are missed, market fit may be off, or employees feel unheard.',
            ],
            win: 'Prospects who are drawn to your conviction.',
            lose:
              'Prospects, and potentially employees, who feel the Defender overpowers their good ideas or opinions.',
            example:
              'Susan feels a great deal of pressure to feel like everything is being covered well and wants to help those around her feel things are under control not realizing she has gone further than intended in defending her choices.',
          },
          {
            title: 'The Copy Catter',
            tips: [
              'Are very thoughtful how they package and convey their proposition to the world encompassing the language, product, team, and testimonials.',
              'Closer inspection reveals approach used is quite similar to competitors and language sounds good because it has become common/not distinctive.',
              'It is easy to slip into marketing speak because it sounds good at the surface.',
            ],
            win:
              'Prospects who are having a problem with their current provider and you catch them at the right time.',
            lose:
              'Prospects that see little that signals them to work with you vs. someone else.',
            example:
              'Fran did the work to build out a solid pitch for the company. The challenge is that all his competitors are saying nearly the same thing so prospects struggle to find a reason to choose one over another or to replace their incumbent provider.',
          },
          {
            title: 'The Schmoozer',
            tips: [
              'Are very good at building a larger network of friends/relationships that blend business and social engagement. They are the fun ones to hang with.',
              'May require incremental technical, prospecting, or closing support.',
              'Possible risk of valuing relationships over conversions and margin.',
            ],
            win:
              'Prospects who like more socially oriented, relational selling.',
            lose:
              'Prospects who don’t want to be sold socially. Decision-makers who are more fact-based than relational may view the social as a conflict of interest.',
            example:
              'Jill is a social person who has always been a natural extrovert and that is where she feels most confident. She extends this approach to her career and over time has built this network that she can introduce to a proposition. Getting to a close can feel a bit awkward and she appreciates support for that.',
          },
          {
            title: 'The Interrogator',
            tips: [
              'Are very good at asking questions and of course asking good questions is an essential quality to winning customers!',
              'The risk occurs for those who ask too many questions too early in their pitch that the prospect feels they have not yet earned the right to ask.',
              'Also, Interrogators can slip into using questioning as a workaround for having a clearly stated value proposition which prospects want to hear.',
            ],
            win:
              'Prospects who accept being deeply questioned from the onset of the sales process as a signal of listening.',
            lose:
              'Prospects who view the questioning as intrusive or want to hear your value proposition earlier without them having to divulge so much time and information.',
            example:
              'Josh is a veteran salesperson who feels most confident with a sales approach that he will customize each time based on his line of questions. Lots of questions makes him more comfortable and he believes this makes the prospect feel like he is there for them. He misses bringing his openness first and helping them know what his product stands for earlier so the prospect has an idea where he might be taking them.',
          },
        ]
      }
      return [
        {
          moreInfo: 'Press here to see more information on our website',
          infoLink: 'https://www.brainhealthvitals.com/nutrition',
          moreVideoInfo: 'For more information check out the video',
          videoInfoLink: 'https://youtu.be/YQkzakP5ufI',
          'Doing well': {
            text:
              'Congratulations! Your score on the Nutrition Lifestyle Factor indicates that you are <strong>Doing Well</strong> in this area of brain health protection! Nutrition is one of the key factors in maintaining overall brain health Your brain is responsible for all the automatic functions of your body such as breathing and keeping your heart beating, as well as memory, attention, your emotions, your thoughts, dreams, and aspirations, to say nothing of all your movements, needs quality fuel to keep it going. High-quality food filled with vitamins, mineral, proteins, and antioxidants will help keep the one hundred million neurons of your brain and their trillions of connections communicating as they should. Research shows nutrition is important for mental health and can reduce risk for brain illness, like anxiety and depression, as well as other disorders. It is also an important protective factor against cognitive decline, stroke, and dementia. So, keep up the good work in the area of nutrition! If you want to continue to build on your nutritional success.',
            moreTips: 'Press here for some additional tips',
            tipsTitle: 'Tips for Further Enhancing Nutrition',
            tips: [
              {
                title: 'Replace butter or margarine with olive oil.',
                description:
                  'Olive oil is a tasty and far more nutritious alternative to butter for cooking, for breads (use for dipping instead of spreading), on pastas, and it’s great for salads.',
              },
              {
                title: 'Eat fruits for dessert.',
                description:
                  'Fruit served with a small amount of cheese or some nuts makes a healthy alternative to sugar-laden desserts. It can soon become a satisfying habit.',
              },
              {
                title:
                  'Replace soft drinks and sport drinks with water or seltzer.',
                description:
                  'Water or seltzer with a squeeze of lemon, or other fruit is a delicious and much healthier alternative to high-sugar soft drinks that are not beneficial to brain functioning. You can also add vegetables, such as cucumbers, to your water for a refreshing change of taste.',
              },
            ],
          },
          'Could improve': {
            text:
              'Your score on the Nutrition Lifestyle Factor indicates that you <strong>Could Improve</strong> in this area to bolster your brain health. Nutrition is one of the key factors in maintaining overall brain health Your brain, which is responsible for all the automatic functions of your body such as breathing and keeping your heart beating, as well as memory, attention, your emotions, your thoughts, dreams, and aspirations, to say nothing of all your movements, needs quality fuel to keep it going.  High-quality food filled with vitamins, mineral, proteins, and antioxidants will help keep the one hundred million neurons of your brain and their trillions of connections communicating as they should. Research shows nutrition is important for mental health and can reduce risk for depression and other disorders. It is also an important protective factor against cognitive decline, stroke, and dementia. Improving your nutrition is extremely important for your current and future brain health.',
            moreTips:
              'Press here for some tips to improve your Nutrition score',
            tipsTitle: 'Tips for Enhancing Nutrition',
            tips: [
              {
                title: 'Replace butter or margarine with olive oil.',
                description:
                  'Olive oil is a tasty and far more nutritious alternative to butter for cooking, for breads (use for dipping instead of spreading), on pastas, and it’s great for salads.',
              },
              {
                title: 'Eat fruits for dessert.',
                description:
                  'Fruit served with a small amount of cheese or some nuts makes a healthy alternative to sugar-laden desserts. It can soon become a satisfying habit.',
              },
              {
                title:
                  'Replace soft drinks and sport drinks with water or seltzer.',
                description:
                  'Water or seltzer with a squeeze of lemon, or other fruit is a delicious and much healthier alternative to high-sugar soft drinks that are not beneficial to brain functioning. You can also add vegetables, such as cucumbers, to your water for a refreshing change of taste.',
              },
            ],
          },
          'Needs improvement': {
            text:
              'Your score on the Nutrition Lifestyle Factor indicates that you <strong>Need to Improve</strong> in this area to bolster your brain health and reduce your risk of brain decline. Nutrition is one of the key factors in maintaining overall brain health Your brain, which is responsible for all the automatic functions of your body such as breathing and keeping your heart beating, as well as memory, attention, your emotions, your thoughts, dreams, and aspirations, to say nothing of all your movements, needs quality fuel to keep it going. High-quality food filled with vitamins, mineral, proteins, and antioxidants will help keep the one hundred million neurons of your brain and their trillions of connections communicating as they should. Research shows nutrition is important for mental health and can reduce risk for depression and other disorders. It is also an important protective factor against cognitive decline, stroke, and dementia. Improving your nutrition is extremely important for your current and future brain health.',
            moreTips:
              'Press here for some tips to improve your Nutrition score',
            tipsTitle: 'Tips for Enhancing Nutrition',
            tips: [
              {
                title: 'Replace butter or margarine with olive oil.',
                description:
                  'Olive oil is a tasty and far more nutritious alternative to butter for cooking, for breads (use for dipping instead of spreading), on pastas, and it’s great for salads.',
              },
              {
                title: 'Eat fruits for dessert.',
                description:
                  'Fruit served with a small amount of cheese or some nuts makes a healthy alternative to sugar-laden desserts. It can soon become a satisfying habit.',
              },
              {
                title:
                  'Replace soft drinks and sport drinks with water or seltzer.',
                description:
                  'Water or seltzer with a squeeze of lemon, or other fruit is a delicious and much healthier alternative to high-sugar soft drinks that are not beneficial to brain functioning. You can also add vegetables, such as cucumbers, to your water for a refreshing change of taste.',
              },
            ],
          },
        },
        {
          moreInfo: 'Press here to see more information on our website',
          infoLink: 'https://www.brainhealthvitals.com/general-health',
          'Doing well': {
            text:
              'Congratulations! Your score on the General Health Lifestyle Factor indicates that you are <strong>Doing Well</strong> in this area of brain health protection! Physical health and brain health are interrelated. In fact, brain health is health. With the expectation of living longer today than we did just several generations ago, it is important to maintain both physical and brain health to ensure a beneficial quality of life. Good general health has a positive effect on brain health and can help reduce stress as well as protect against mental disorder and cognitive decline. Even if you have (or have previously dealt with) a chronic disease, if it is under medical control, you can still experience good health if you adhere to positive lifestyle practices. For example, keeping body weight in the healthy range and not smoking are major contributors to positive health. Below is a checklist of positive practices to keep your brain/body in the good General Health range.',
            moreTips:
              'Press here for a checklist of positive practices to keep your brain/body in the good General Health range',
            tipsTitle: 'Checklist for Positive General Health',
            tips: [
              {
                title: 'Keep weight in the positive BMI range (18.5 – 25.0)',
              },
              {
                title: 'Do not smoke',
              },
              {
                title: 'Do not use tobacco products (vaping, chew, cigars)',
              },
              {
                title: 'Drink alcohol in moderation ',
              },
              {
                title: 'Practice yoga or tai chi for strength and flexibility',
              },
              {
                title:
                  'Practice effective non-opioid chronic pain management strategies',
              },
              {
                title:
                  'See Physical Exercise, Sleep, and Nutrition factors for more positive lifestyle tips',
              },
            ],
          },
          'Could improve': {
            text:
              'Your score on the General Health Lifestyle Factor indicates that you <strong>Could Improve</strong> in this area to bolster your brain health. Physical health and brain health are interrelated. In fact, brain health is health. With the expectation of living longer today than we did just several generations ago, it is important to maintain this physical/brain health symbiosis to ensure a beneficial quality of life. Good general health has a positive effect on brain health and can help reduce stress as well as protect against mental disorder and cognitive decline. Even if you have (or have previously dealt with) a chronic disease, if it is under medical control, you can still experience good health if you adhere to positive lifestyle practices. For example, keeping body weight in the healthy range and not smoking are major contributors to positive health.',
            moreTips: 'Press here for some tips to improve your General Health',
            tipsTitle: 'Advice for Improving General Health',
            tips: [
              {
                title: 'Maintain a healthy weight.',
                description:
                  'One of the biggest burdens on general health is overweight and/or obesity. Obesity and overweight are associated with increased risk for dementia in older age, with stroke, decreased cognitive function, and depression. Likewise, being severely underweight is associated with reduced brain volume and depression. Although it is an imperfect measurement, body mass index (BMI: calculated as 703 times height in inches divided by the square of weight in pounds) of 18.5 to 25 is considered a healthy range. You can work toward this BMI by eating a healthy diet. We recommend a plan based on the Mediterranean diet.',
              },
              {
                title: 'Do not smoke or use tobacco products.',
                description:
                  "One of the best things you can do for your health is to keep it tobacco-free. Even if you have tried unsuccessfully in the past to quit smoking, don’t give up. Research indicates that it takes 12-14 or even more attempts at quitting before a person becomes a successful non-smoker. So if you’ve tried to quit and are still smoking, that’s normal. Just keep at it. Keep reading about it (smoke (advice from colleagues at Harvard Medical School <a href='https://www.health.harvard.edu/blog/whats-best-way-quit-smoking-201607089935' target='_blank'>https://www.health.harvard.edu/blog/whats-best-way-quit-smoking-201607089935</a>) and working on it. You will succeed!",
              },
              {
                title: 'Practice tai chi or yoga.',
                description:
                  "Tai chi and yoga are representative of mind-body (MB) exercises that have been shown to be beneficial for immune functioning and endocrine functioning as well as stress reduction and physical toning. MB exercises are evidence-based methods of improving overall health and well-being. If you are not practicing a form of MB exercise, you can learn more about them here:<br/><a href='https://www.health.harvard.edu/mind-and-mood/introduction-to-yoga-breathing-exercises' target='_blank'>https://www.health.harvard.edu/mind-and-mood/introduction-to-yoga-breathing-exercises</a><br/><a href='https://www.health.harvard.edu/staying-healthy/the-health-benefits-of-tai-chi' target='_blank'>https://www.health.harvard.edu/staying-healthy/the-health-benefits-of-tai-chi</a>",
              },
            ],
          },
          'Needs improvement': {
            text:
              'Your score on the General Health Lifestyle Factor indicates that you <strong>Need to Improve</strong> in this area to bolster your brain health and reduce your risk of brain decline. Physical health and brain health are interrelated. In fact, brain health is health. With the expectation of living longer today than we did just several generations ago, it is important to maintain this physical/brain health symbiosis to ensure a beneficial quality of life. Good general health has a positive effect on brain health and can help reduce stress as well as protect against mental disorder and cognitive decline. Even if you have (or have previously dealt with) a chronic disease, if it is under medical control, you can still experience of good health if you adhere to positive lifestyle practices. For example, keeping body weight in the healthy range and not smoking are major contributors to positive health.',
            moreTips: 'Press here for some tips to improve your General Health',
            tipsTitle: 'Advice for Improving General Health',
            tips: [
              {
                title: 'Maintain a healthy weight.',
                description:
                  'One of the biggest burdens on general health is overweight and/or obesity. Obesity and overweight are associated with increased risk for dementia in older age, with stroke, decreased cognitive function, and depression. Likewise, being severely underweight is associated with reduced brain volume and depression. Although it is an imperfect measurement, body mass index (BMI: calculated as 703 times height in inches divided by the square of weight in pounds) of 18.5 to 25 is considered a healthy range. You can work toward this BMI by eating a healthy diet. We recommend a plan based on the Mediterranean diet.',
              },
              {
                title: 'Do not smoke or use tobacco products.',
                description:
                  "One of the best things you can do for your health is to keep it tobacco-free. Even if you have tried unsuccessfully in the past to quit smoking, don’t give up. Research indicates that it takes 12-14 or even more attempts at quitting before a person becomes a successful non-smoker. So, if you’ve tried to quit and are still smoking, that’s normal. Just keep at it. Keep reading about it (smoke (advice from colleagues at Harvard Medical School <a href='https://www.health.harvard.edu/blog/whats-best-way-quit-smoking-201607089935' target='_blank'>https://www.health.harvard.edu/blog/whats-best-way-quit-smoking-201607089935</a>) and working on it. You will succeed!",
              },
              {
                title: 'Practice tai chi or yoga.',
                description:
                  "Tai chi and yoga are representative of mind-body (MB) exercises that have been shown to be beneficial for immune functioning and endocrine functioning as well as stress reduction and physical toning. MB exercises are evidence-based methods of improving overall health and well-being. If you are not practicing a form of MB exercise, you can learn more about them here: <br/><a href='https://www.health.harvard.edu/mind-and-mood/introduction-to-yoga-breathing-exercises' target='_blank'>https://www.health.harvard.edu/mind-and-mood/introduction-to-yoga-breathing-exercises</a><br/><a href='https://www.health.harvard.edu/staying-healthy/the-health-benefits-of-tai-chi' target='_blank'>https://www.health.harvard.edu/staying-healthy/the-health-benefits-of-tai-chi</a>",
              },
            ],
          },
        },
        {
          moreInfo: 'Press here to see more information on our website',
          infoLink: 'https://www.brainhealthvitals.com/physical-activity',
          moreVideoInfo: 'For more information check out the video',
          videoInfoLink: 'https://youtu.be/A18zzTlrjj4',
          'Doing well': {
            text:
              'Congratulations! Your score on the Physical Activity Lifestyle Factor indicates that you are <strong>Doing Well</strong> in this area of brain health protection! Physical activity is not only important to your overall bodily health, it is vital for brain health as well! Research shows that physical activity and exercise are associated with increased neuroplasticity (the ability of the brain to change and repair itself in response to learning or injury), with increased release of an  all-important protein brain-derived neurotrophic factor (BDNF), and with reduced risk for dementia and cognitive decline. In fact, physical exercise is one of the best things you can do to optimize brain health. Keep up the good work with physical activity and exercise!',
            moreTips:
              'Press here to see additional tips on improving Physical Activity',
            tipsTitle: 'Tips for Further Enhancing Physical Activity',
            tips: [
              {
                title: 'Improve sedentary work habits.',
                description:
                  'If you typically sit at a desk or in front of a computer for hours each day, try one of the following: either set your computer or phone to alert you each hour. This is your cue to get up, stretch and take a short walk or exercise for five minutes. Five minutes of exercise each hour will help circulation and oxygen flow to the brain. Alternately, you can invest in a standing desk, which will allow you to stand and move your feet while you are working, again keeping that oxygen flowing to your brain.',
              },
              {
                title: 'Mix it up.',
                description:
                  'Alternate the type of activity you engage to include strength training and to build flexibility and endurance.',
              },
              {
                title: 'Take the stairs.',
                description:
                  'If your destination is only one to three floors from your current location, take the stairs rather than waiting for an elevator, assuming you can safely do so.',
              },
              {
                title: 'Take advantage of TV time:',
                description:
                  'You can engage in isometric exercises, use hand weights, or even jog in place, as a break from the couch potato position in front of the TV.',
              },
            ],
          },
          'Could improve': {
            text:
              'Your score on the Physical Activity Lifestyle Factor indicates that you <strong>Could Improve</strong> in this area to bolster your brain health. Physical activity is not only important to your overall bodily health, it is vital for brain health as well! Research shows that physical activity and exercise are associated with increased neuroplasticity (the ability of the brain to change and repair itself in response to learning or injury), with increased release of an all-important protein brain-derived neurotrophic factor (BDNF), and with reduced risk for dementia and cognitive decline. In fact, physical exercise is one of the best things you can do to optimize brain health.',
            moreTips:
              'Press here to see additional tips on improving Physical Activity',
            tipsTitle: 'Tips for Enhancing Physical Activity',
            tips: [
              {
                title: 'Improve sedentary work habits.',
                description:
                  'If you typically sit at a desk or in front of a computer for hours each day, try one of the following: either set your computer or phone to alert you each hour. This is your cue to get up, stretch and take a short walk or exercise for five minutes. Five minutes of exercise each hour will help circulation and oxygen flow to the brain. Alternately, you can invest in a standing desk, which will allow you to stand and move your feet while you are working, again keeping that oxygen flowing to your brain.',
              },
              {
                title: 'Take the stairs.',
                description:
                  'If your destination is only one to three floors from your current location, take the stairs rather than waiting for an elevator, assuming you can safely do so.',
              },
              {
                title: 'Start slow and increase your exercise time.',
                description:
                  'If you are not currently getting regular exercise, start with what you can do, even if it’s just a walk to the end of the driveway and back. Gradually add time and distance to your walks until you reach 45-50 minutes several times per week of moderately invigorating exercise.',
              },
              {
                title: 'Take advantage of TV time:',
                description:
                  'You can engage in isometric exercises, use hand weights, or even jog in place, as a break from the couch potato position in front of the TV.',
              },
            ],
          },
          'Needs improvement': {
            text:
              'Your score on the Physical Activity Lifestyle Factor indicates that you <strong>Need to Improve</strong> in this area to bolster your brain health and reduce your risk of brain decline. Physical activity is not only important to your overall bodily health, it is vital for brain health as well! Research shows that physical activity and exercise are associated with increased neuroplasticity (the ability of the brain to change and repair itself in response to learning or injury), with increased release of the all-important protein brain-derived neurotrophic factor (BDNF), and with reduced risk for dementia and cognitive decline. In fact, physical exercise is one of the best things you can do to optimize brain health. To start your road to improvement in this area, here are some tips for improving Physical Activity.',
            moreTips: 'Press here to see tips',
            tipsTitle: 'Tips for Enhancing Physical Activity',
            tips: [
              {
                title: 'Improve sedentary work habits.',
                description:
                  'If you typically sit at a desk or in front of a computer for hours each day, try one of the following: either set your computer or phone to alert you each hour. This is your cue to get up, stretch and take a short walk or exercise for five minutes. Five minutes of exercise each hour will help circulation and oxygen flow to the brain. Alternately, you can invest in a standing desk, which will allow you to stand and move your feet while you are working, again keeping that oxygen flowing to your brain.',
              },
              {
                title: 'Start slow and increase your exercise time.',
                description:
                  'If you are not currently getting regular exercise, start with what you can do, even if it’s just a walk to the end of the driveway and back. Gradually add time and distance to your walks until you reach 45-50 minutes several times per week of moderately invigorating exercise.',
              },
              {
                title: 'Take the stairs.',
                description:
                  'If your destination is only one to three floors from your current location, take the stairs rather than waiting for an elevator, assuming you can safely do so.',
              },
              {
                title: 'Take advantage of TV time:',
                description:
                  'You can engage in isometric exercises, use hand weights, or even jog in place, as a break from the couch potato position in front of the TV.',
              },
            ],
          },
        },
        {
          moreInfo: 'Press here to see more information on our website',
          infoLink: 'https://www.brainhealthvitals.com/emotional-well-being',
          moreVideoInfo: 'For more information check out the video',
          videoInfoLink: 'https://youtu.be/iZaD9efUF5c',
          'Doing well': {
            text:
              'Congratulations! Your score on the Emotional Well-Being Lifestyle Factor indicates that you are <strong>Doing Well</strong> in this area of brain health protection! Emotional well-being is a general state characterized by positive emotions. As a person with high emotional well-being, you can still experience negative emotions, but you can regulate them and return to a positive state quickly. Although emotional well-being is its own reward, you’ll be happy to know it confers additional benefits for brain health in the form of protection against many forms of mental disorder such as depression and anxiety issues, as well as improving your response to stress and reducing risk for early mortality. There are many ways to develop and sustain emotional well-being',
            moreTips:
              'To continue to keep your well-being in high gear, press here for some tips from our experts',
            tipsTitle: 'Tips for Enhancing Emotional Well-Being',
            tips: [
              {
                title: 'Practice gratitude.',
                description:
                  'To increase your positive emotions, one evidence-based practice is to end each day S writing down three to five things, large or small, you were grateful for that day. Gratitude cannot coexist in the same mental space with chronic negative feelings such as bitterness or despair.',
              },
              {
                title: 'Spend time in places of natural beauty.',
                description:
                  'A walk in the woods, on the beach, or other place of natural beauty can awaken positive emotions, reduce the stress hormone cortisol, and even aid cognitive functioning.',
              },
              {
                title: 'Mindfulness meditation.',
                description:
                  'Mindfulness meditations cultivate non-judgmental awareness of the present moment. General instructions include relaxing and focusing on the flow of the breath or some other aspect of your environment, working up to a period of 20 minutes, nonjudgmentally acknowledging any passing thoughts and then letting them go. Numerous reviews have shown that mindfulness meditations can improve wellbeing.',
              },
            ],
          },
          'Could improve': {
            text:
              'Your score on the Emotional Well-Being Lifestyle Factor indicates that you <strong>Could Improve</strong> in this area to bolster your brain health Emotional well-being is a general state characterized by positive emotions. When you are in a state of emotional well-being, you still experience negative emotions, but you can regulate them and return to a positive state quickly. Although emotional well-being is its own reward, you’ll be happy to know it confers additional benefits for brain health in the form of protection against many forms of mental disorder such as depression and anxiety issues, as well as improving your response to stress and reducing risk for early mortality. There are many ways to develop and sustain emotional well-being.',
            moreTips:
              'Press here for some tips to improve your Emotional Well-Being',
            tipsTitle: 'Tips for Enhancing Emotional Well-Being',
            tips: [
              {
                title: 'Practice gratitude.',
                description:
                  'To increase your positive emotions, one evidence-based practice is to end each day S writing down three to five things, large or small, you were grateful for that day. Gratitude cannot coexist in the same mental space with chronic negative feelings such as bitterness or despair.',
              },
              {
                title: 'Spend time in places of natural beauty.',
                description:
                  'A walk in the woods, on the beach, or other place of natural beauty can awaken positive emotions, reduce the stress hormone cortisol, and even aid cognitive functioning.',
              },
              {
                title: 'Mindfulness meditation.',
                description:
                  'Mindfulness meditations cultivate non-judgmental awareness of the present moment. General instructions include relaxing and focusing on the flow of the breath or some other aspect of your environment, working up to a period of 20 minutes, nonjudgmentally acknowledging any passing thoughts and then letting them go. Numerous reviews have shown that mindfulness meditations can improve well-being.',
              },
            ],
          },
          'Needs improvement': {
            text:
              'Your score on the Emotional Well-Being Lifestyle Factor indicates that you <strong>Need to Improve</strong> in this area to bolster your brain health and reduce your risk of brain decline. Emotional well-being is a general state characterized by positive emotions. When you are in a state of emotional well-being, you still experience negative emotions, but you can regulate them and return to a positive state quickly. Although emotional well-being is its own reward, you’ll be happy to know it confers additional benefits for brain health in the form of protection against many forms of mental disorder such as depression and anxiety issues, as well as improving your response to stress and reducing risk for early mortality. There are many ways to develop and sustain emotional well-being.',
            moreTips: 'Press here for some tips from our experts',
            tipsTitle: 'Tips for Enhancing Emotional Well-Being',
            tips: [
              {
                title: 'Practice gratitude.',
                description:
                  'To increase your positive emotions, one evidence-based practice is to end each day S writing down three to five things, large or small, you were grateful for that day. Gratitude cannot coexist in the same mental space with chronic negative feelings such as bitterness or despair.',
              },
              {
                title: 'Spend time in places of natural beauty.',
                description:
                  'A walk in the woods, on the beach, or other place of natural beauty can awaken positive emotions, reduce the stress hormone cortisol, and even aid cognitive functioning.',
              },
              {
                title: 'Mindfulness meditation.',
                description:
                  'Mindfulness meditations cultivate non-judgmental awareness of the present moment. General instructions include relaxing and focusing on the flow of the breath or some other aspect of your environment, working up to a period of 20 minutes, nonjudgmentally acknowledging any passing thoughts and then letting them go. Numerous reviews have shown that mindfulness meditations can improve wellbeing.',
              },
            ],
          },
        },
        {
          moreInfo: 'Press here to see more information on our website',
          infoLink: 'https://www.brainhealthvitals.com/stress-resilience',
          moreVideoInfo: 'For more information check out the video',
          videoInfoLink: 'https://youtu.be/3bnQ2NIlJho',
          'Doing well': {
            text:
              'Congratulations! Your score on the Stress Resilience Lifestyle Factor indicates that you are <strong>Doing Well</strong> in this area of brain health protection! Stress resilience is important to brain development, as well as brain functioning and performance. Research shows that individuals who are high in stress resilience have more brain plasticity (the ability of the brain to learn, grow, and adapt) than those who are less resilient. Stress resilience is protective against many mental illnesses, including depression, and it is also protective against inflammation. Stress has negative effects on the brain; learning to deal with stress in a resilient way benefits the brain throughout the lifespan. You scored high in stress resilience.',
            moreTips:
              'To keep your resilience high, press here for some additional tips',
            tipsTitle: 'Tips for Further Enhancing Stress Resilience',
            tips: [
              {
                title: 'Practice deep breathing:',
                description:
                  'Slow, deep breathing, from the diaphragm rather than the chest, is a sure-fire way to reduce stress-related activation of the automatic nervous system. This lowers your biological response to stress. Once you have your biological response under control, you can think more clearly about how to handle stressful situations.',
              },
              {
                title: 'Use problem-focused coping:',
                description:
                  'Many people respond to stress by trying to control their emotional response (known as emotion-focused coping) rather than dealing with the problem (stressor) that caused the stress to begin with. Dealing with the stressor directly rather than avoiding it is associated with greater stress resilience.',
              },
              {
                title: 'Remember your past successes.',
                description:
                  'Write down and keep a file of times you faced difficult situations and handled them well. When you face current stressful situations, read over or remind yourself of your past successes. This will reinforce your belief in your ability to successfully deal with the current situation and give you confidence to move forward.',
              },
            ],
          },
          'Could improve': {
            text:
              'Your score on the Stress Resilience Lifestyle Factor indicates that you <strong>Could Improve</strong> in this area to bolster your brain health. Stress resilience is important to brain development, as well as brain functioning and performance. Research shows that individuals who are high in stress resilience who encounter adversity early in life have more brain plasticity than those who are less resilient. Stress resilience is protective against many mental illnesses, including depression, and it is also protective against inflammation. Stress has negative effects on the brain; learning to deal with stress in a resilient way benefits the brain throughout the lifespan.',
            moreTips:
              'Press here for some tips to improve your Resilience score',
            tipsTitle: 'Tips for Enhancing Stress Resilience',
            tips: [
              {
                title: 'Practice deep breathing:',
                description:
                  'Slow, deep breathing, from the diaphragm rather than the chest, is a sure-fire way to reduce stress-related activation of the automatic nervous system. This lowers your biological response to stress. Once you have your biological response under control, you can think more clearly about how to handle stressful situations.',
              },
              {
                title: 'Use problem-focused coping:',
                description:
                  'Many people respond to stress by trying to control their emotional response (known as emotion-focused coping) rather than dealing with the problem (stressor) that caused the stress to begin with. Dealing with the stressor directly rather than avoiding it is associated with greater stress resilience.',
              },
              {
                title: 'Remember your past successes.',
                description:
                  'Write down and keep a file of times you faced difficult situations and handled them well. When you face current stressful situations, read over or remind yourself of your past successes. This will reinforce your belief in your ability to successfully deal with the current situation and give you confidence to move forward.',
              },
            ],
          },
          'Needs improvement': {
            text:
              'Your score on the Stress Resilience Lifestyle Factor indicates that you <strong>Need to Improve</strong> in this area to bolster your brain health and reduce your risk of brain decline. Stress resilience is important to brain development, as well as brain functioning and performance. Research shows that individuals who are high in stress resilience who encounter adversity early in life have more brain plasticity than those who are less resilient. Stress resilience is protective against many mental illnesses, including depression, and it is also protective against inflammation. Stress has negative effects on the brain; learning to deal with stress in a resilient way benefits the brain throughout the lifespan.',
            moreTips:
              'Press here for some tips to improve your Resilience score',
            tipsTitle: 'Tips for Enhancing Stress Resilience',
            tips: [
              {
                title: 'Practice deep breathing:',
                description:
                  'Slow, deep breathing, from the diaphragm rather than the chest, is a sure-fire way to reduce stress-related activation of the automatic nervous system. This lowers your biological response to stress. Once you have your biological response under control, you can think more clearly about how to handle stressful situations.',
              },
              {
                title: 'Use problem-focused coping:',
                description:
                  'Many people respond to stress by trying to control their emotional response (known as emotion-focused coping) rather than dealing with the problem (stressor) that caused the stress to begin with. Dealing with the stressor directly rather than avoiding it is associated with greater stress resilience.',
              },
              {
                title: 'Remember your past successes.',
                description:
                  'Write down and keep a file of times you faced difficult situations and handled them well. When you face current stressful situations, read over or remind yourself of your past successes. This will reinforce your belief in your ability to successfully deal with the current situation and give you confidence to move forward.',
              },
            ],
          },
        },
        {
          moreInfo: 'Press here to see more information on our website',
          infoLink: 'https://www.brainhealthvitals.com/sleep',
          moreVideoInfo: 'For more information check out the video',
          videoInfoLink: 'https://youtu.be/IxCV6VwGa_Q',
          'Doing well': {
            text:
              'Congratulations! Your score on the Sleep Lifestyle Factor indicates that you are <strong>Doing Well</strong> in this area of brain health protection! Sleep is important for a healthy brain. Although technically the brain never sleeps, the fact that we are in a different state of consciousness during sleep allows the brain to perform some important functions, such as clearing out toxic biproducts, memory consolidation, renewal of energy, and other work. Research indicates that sleep is important for current brain function and mental health. It is also a protective factor for future brain health. Poor sleep is associated with risk for Alzheimer’s disease, dementia, and cognitive decline. While sleeping patterns naturally change as we age, you can continue to get good sleep regardless of your age.',
            moreTips:
              'Press here if you would like some additional tips on building good sleep habits from our sleep experts',
            tipsTitle: 'Tips for Further Enhancing Sleep',
            tips: [
              {
                title: 'Wake up at the same time each day.',
                description:
                  'Regardless of when you go to bed at night, try to wake up at the same time each day. This has been shown to keep the sleep/wake cycle more regular.',
              },
              {
                title: 'Keep your sleep space cool and dark.',
                description:
                  'Darker spaces and cooler temperatures promote better sleep. If you’re cold, just use an additional blanket.',
              },
              {
                title:
                  'Get at least 20 minutes of sunshine earlier in the day.',
                description:
                  'Early sunshine promotes appropriate circadian rhythms which in turn promote healthy sleep/wake cycles.',
              },
            ],
          },
          'Could improve': {
            text:
              'Your score on the Sleep Lifestyle Factor indicates that you <strong>Could Improve</strong> in this area to bolster your brain health. Sleep is important for a healthy brain. Although technically the brain never sleeps, the fact that we are in a different state of consciousness during sleep allows the brain to perform some important functions, such as clearing out toxic biproducts, memory consolidation, renewal of energy, and other work. Research indicates that sleep is important for current brain function and mental health. It is also a protective factor for future brain health. Poor sleep is associated with risk for Alzheimer’s disease, dementia, and cognitive decline. While sleeping patterns naturally change as we age, you can continue to get good sleep regardless of your age.',
            moreTips: 'Press here for some tips to improve your Sleep score',
            tipsTitle: 'Tips for Enhancing Sleep',
            tips: [
              {
                title: 'Wake up at the same time each day.',
                description:
                  'Regardless of when you go to bed at night, try to wake up at the same time each day. This has been shown to keep the sleep/wake cycle more regular.',
              },
              {
                title: 'Keep your sleep space cool and dark.',
                description:
                  'Darker spaces and cooler temperatures promote better sleep. If you’re cold, just use an additional blanket.',
              },
              {
                title:
                  'Get at least 15 minutes of sunshine earlier in the day.',
                description:
                  'Early sunshine promotes appropriate circadian rhythms which in turn promote healthy sleep/wake cycles.',
              },
              {
                title: 'Strive for 7 to 9 hours of sleep per night.',
                description:
                  'An occasional late night or even a missed night of sleep will not be catastrophic to your brain health. However, averaging 7 to 9 hours per night should be the goal for adults to give your brain its optimal rest period.',
              },
            ],
          },
          'Needs improvement': {
            text:
              'Your score on the Sleep Lifestyle Factor indicates that you <strong>Need to Improve</strong> in this area to bolster your brain health and reduce your risk of brain decline. Sleep is important for a healthy brain. Although technically the brain never sleeps, the fact that we are in a different state of consciousness during sleep allows the brain to perform some important functions, such as clearing out toxic biproducts, memory consolidation, renewal of energy, and other work. Research indicates that sleep is important for current brain function and mental health. It is also a protective factor for future brain health. Poor sleep is associated with risk for Alzheimer’s disease, dementia, and cognitive decline. While sleeping patterns naturally change as we age, you can continue to get good sleep regardless of your age.',
            moreTips: 'Press here for some tips to improve your Sleep score',
            tipsTitle: 'Tips for Enhancing Sleep',
            tips: [
              {
                title: 'Wake up at the same time each day.',
                description:
                  'Regardless of when you go to bed at night, try to wake up at the same time each day. This has been shown to keep the sleep/wake cycle more regular.',
              },
              {
                title: 'Keep your sleep space cool and dark.',
                description:
                  'Darker spaces and cooler temperatures promote better sleep. If you’re cold, just use an additional blanket.',
              },
              {
                title:
                  'Get at least 15 minutes of sunshine earlier in the day.',
                description:
                  'Early sunshine promotes appropriate circadian rhythms which in turn promote healthy sleep/wake cycles.',
              },
              {
                title: 'Strive for 7 to 9 hours of sleep per night.',
                description:
                  'An occasional late night or even a missed night of sleep will not be catastrophic to your brain health. However, averaging 7 to 9 hours per night should be the goal for adults to give your brain its optimal rest period.',
              },
            ],
          },
        },
        {
          moreInfo: 'Press here to see more information on our website',
          infoLink: 'https://www.brainhealthvitals.com/meaning-purpose',
          moreVideoInfo: 'For more information check out the video',
          videoInfoLink: 'https://youtu.be/wdzpAL4jaOU',
          'Doing well': {
            text:
              'Congratulations! Your score on the Meaning and Purpose Lifestyle Factor indicates that you are <strong>Doing Well</strong> in this area of brain health protection! Having a strong sense of meaning and purpose in life contributes to well-being in a way that is separate from what we think of as emotional well-being (or feeling happy and contented). Like emotional well-being, a sense of meaning and purpose is its own reward in the current moment. However, having a sense of meaning and purpose has been shown to also improve brain function, especially later in life, and to reduce risk for dementia, brain tissue damage (such as stroke), and cognitive decline. So, keep up the good work here!',
            moreTips:
              'Press here for some tips to further improve your sense of meaning and purpose',
            tipsTitle: 'Tips for Enhancing Meaning and Purpose',
            tips: [
              {
                title: 'Acts of kindness.',
                description:
                  'Research shows that helping others increases our sense of purpose as well as increasing positive emotions. To match the research task, perform five acts of kindness—all within one day of the week—for six weeks. The acts can be anything that benefits others or that makes another happy (such as donating blood, visiting an elderly relative).',
              },
              {
                title: 'Learn a new skill.',
                description:
                  'Learning something new that is somewhat challenging, such as a new language or playing a musical instrument, brings immediate purpose to your life and increases cognitive engagement.',
              },
              {
                title:
                  'Spend time thinking about what is important and then take steps to improve it.',
                description:
                  'Often we don’t take the time to think about what we really find important in our lives. And if we do think consciously about this, we may see that there are problems, but we don’t take action to improve them. First, make a list of 5 to 8 things that are most important in your life. Then write down areas where each of these things could be improved. Make it your mission to improve at least one of those areas.',
              },
            ],
          },
          'Could improve': {
            text:
              'Your score on the Meaning and Purpose Lifestyle Factor indicates that you <strong>Could Improve</strong> in this area to bolster your brain health. Having a strong sense of meaning and purpose in life contributes to well-being in a way that is separate from what we think of as emotional well-being (or feeling happy and contented). Like emotional well-being, a sense of meaning and purpose is its own reward in the current moment. However, having a sense of meaning and purpose has been shown to also improve brain function, especially later in life, and to reduce risk for dementia, brain tissue damage (such as stroke), and cognitive decline.',
            moreTips:
              'Press here for some tips to improve your sense of meaning and purpose',
            tipsTitle: 'Tips for Enhancing Meaning and Purpose',
            tips: [
              {
                title: 'Acts of kindness.',
                description:
                  'Research shows that helping others increases our sense of purpose as well as increasing positive emotions. To match the research task, perform five acts of kindness—all within one day of the week—for six weeks. The acts can be anything that benefits others or that makes another happy (such as donating blood, visiting an elderly relative).',
              },
              {
                title: 'Learn a new skill.',
                description:
                  'Learning something new that is somewhat challenging, such as a new language or playing a musical instrument, brings immediate purpose to your life and increases cognitive engagement.',
              },
              {
                title:
                  'Spend time thinking about what is important and then take steps to improve it.',
                description:
                  'Often we don’t take the time to think about what we really find important in our lives. And if we do think consciously about this, we may see that there are problems, but we don’t take action to improve them. First, make a list of 5 to 8 things that are most important in your life. Then write down areas where each of these things could be improved. Make it your mission to improve at least one of those areas.',
              },
            ],
          },
          'Needs improvement': {
            text:
              'Your score on the Meaning and Purpose Lifestyle Factor indicates that you <strong>Need to Improve</strong> in this area to bolster your brain health and reduce your risk of brain decline. Having a strong sense of meaning and purpose in life contributes to well-being in a way that is separate from what we think of as emotional well-being (or feeling happy and contented). Like emotional well-being, a sense of meaning and purpose is its own reward in the current moment. However, having a sense of meaning and purpose has been shown to also improve brain function, especially later in life, and to reduce risk for dementia, brain tissue damage (such as stroke), and cognitive decline.',
            moreTips:
              'Press here for some tips to improve your sense of meaning and purpose',
            tipsTitle: 'Tips for Enhancing Meaning and Purpose',
            tips: [
              {
                title: 'Acts of kindness.',
                description:
                  'Research shows that helping others increases our sense of purpose as well as increasing positive emotions. To match the research task, perform five acts of kindness—all within one day of the week—for six weeks. The acts can be anything that benefits others or that makes another happy (such as donating blood, visiting an elderly relative).',
              },
              {
                title: 'Learn a new skill.',
                description:
                  'Learning something new that is somewhat challenging, such as a new language or playing a musical instrument, brings immediate purpose to your life and increases cognitive engagement.',
              },
              {
                title:
                  'Spend time thinking about what is important and then take steps to improve it.',
                description:
                  'Often we don’t take the time to think about what we really find important in our lives. And if we do think consciously about this, we may see that there are problems, but we don’t take action to improve them. First, make a list of 5 to 8 things that are most important in your life. Then write down areas where each of these things could be improved. Make it your mission to improve at least one of those areas.',
              },
            ],
          },
        },
        {
          moreInfo: 'Press here to see more information on our website',
          infoLink: 'https://www.brainhealthvitals.com/social-connection',
          moreVideoInfo: 'For more information check out the video',
          videoInfoLink: 'https://youtu.be/OVrs61O2E6I',
          'Doing well': {
            text:
              'Congratulations! Your score on the Social Connections Lifestyle Factor indicates that you are <strong>Doing Well</strong> in this area of brain health protection! Social connections and social support are a strong protective factor against all types of mental disorders, including depression, anxiety disorders, schizophrenia, bipolar disorder, and post-traumatic stress disorder. Social connections and support also help protect the brain from stress, inflammation, and cognitive decline. Keep up the good work with social connections and support!',
            moreTips:
              'If you want to continue to build on your success, press here for some additional tips',
            tipsTitle: 'Tips for Enhancing Social Connections',
            tips: [
              {
                title: 'Join and Engage:',
                description:
                  'You can continue to enlarge your social support circle by joining a hobby-based group (such as a book club, quilting group, boating group – check the internet for groups in your area or start a group yourself!) or a community organization (such as a community garden, sports team, faith-based organization, choral group, or political group). Best of all, you can volunteer (hospital, animal shelter, senior center, school, nature preserve, emergency services).',
              },
              {
                title: 'Improve your social skills:',
                description:
                  'As someone with a good Social Connections Lifestyle Factor score, you likely have good social skills. But there is always room for improvement. If you’re interested, check the internet for books, classes, and articles on improving these areas that have been shown to be important in personal relationships: communication style (assertive communication), listening skills (such as reflective listening), small talk and conversation, nonverbal expression, and conflict resolution skills, to name a few.',
              },
            ],
          },
          'Could improve': {
            text:
              'Your score on the Social Connections Lifestyle Factor indicates that you <strong>Could Improve</strong> in this area to bolster your brain health. Social connections and social support are a strong protective factor against all types of mental disorders, including depression, anxiety disorders, schizophrenia, bipolar disorder, and post-traumatic stress disorder. Social connections and support also help protect the brain from stress, inflammation, and cognitive decline. Your goal is to increase you Social Connections score so that you are in the Doing Well category.',
            moreTips: 'Press here for some tips to improve Social Connections',
            tipsTitle: 'Tips for Enhancing Social Connections',
            tips: [
              {
                title: 'Schedule social time into your calendar.',
                description:
                  'Many people claim they just don’t have time for socializing due to work, childcare, chores, and other demands. But social time is important to your physical and your brain health. Caring for the heath of your brain is crucial to your performance at work and in all important areas of life endeavor, as successful CEOs are aware. So, to enhance overall brain performance, block off time to spend with family and friends.',
              },
              {
                title: 'Join and Engage:',
                description:
                  'If your social support circle is small, you can enlarge it by joining a hobby-based group (such as a book club, quilting group, boating group – check the internet for groups in your area) or a community organization (such as a community garden, sports team, faith-based organization, choral group, or political group). Best of all, you can volunteer (hospital, animal shelter, senior center, school, nature preserve, emergency services).',
              },
              {
                title: 'Improve your social skills:',
                description:
                  'Check the internet for books, classes, and articles on improving your communication style, listening skills, small talk and conversation, nonverbal, and conflict resolution skills, to name a few.',
              },
            ],
          },
          'Needs improvement': {
            text:
              'Your score on the Social Connections Lifestyle Factor indicates that you <strong>Need to Improve</strong> in this area to bolster your brain health and reduce your risk of brain decline. Social connections and social support are a strong protective factor against all types of mental disorders, including depression, anxiety disorders, schizophrenia, bipolar disorder, and post-traumatic stress disorder. Social connections and support also help protect the brain from stress, inflammation, and cognitive decline. Your goal is to increase you Social Connections score and elevate yourself out of the Need to Improve category.',
            moreTips: 'Press here for some tips to improve Social Connections',
            tipsTitle: 'Tips for Enhancing Social Connections',
            tips: [
              {
                title: 'Schedule social time into your calendar.',
                description:
                  'Many people claim they just don’t have time for socializing due to work, childcare, chores, and other demands. But social time is important to your physical and your brain health. Caring for the heath of your brain is crucial to your performance at work and in all important areas of life endeavor, as successful CEOs are aware. So, to enhance overall brain performance, block off time to spend with family and friends.',
              },
              {
                title: 'Join and Engage:',
                description:
                  'If your social support circle is small, you can enlarge it by joining a hobby-based group (such as a book club, quilting group, boating group – check the internet for groups in your area) or a community organization (such as a community garden, sports team, faith-based organization, choral group, or political group). Best of all, you can volunteer (hospital, animal shelter, senior center, school, nature preserve, emergency services).',
              },
              {
                title: 'Improve your social skills:',
                description:
                  'Check the internet for books, classes, and articles on improving your communication style, listening skills, small talk and conversation, nonverbal, and conflict resolution skills, to name a few.',
              },
            ],
          },
        },
        {
          moreInfo: 'Press here to see more information on our website',
          infoLink: 'https://www.brainhealthvitals.com/cognitive-stimulation',
          moreVideoInfo: 'For more information check out the video',
          videoInfoLink: 'https://youtu.be/R-o9geeMxDY',
          'Doing well': {
            text:
              'Congratulations! Your score on the Cognitive Stimulation Lifestyle Factor indicates that you are <strong>Doing Well</strong> in this area of brain health protection! Cognitive stimulation is important for keeping the brain healthy throughout the lifespan. Cognitive stimulation early in life helps build cognitive reserve, the ability of the brain fight off symptoms of degenerative brain changes associated with dementia or other brain diseases,. Later in life, it keeps the brain supple and improves plasticity. It is important for staving off cognitive decline. Cognitive stimulation is also associated with increased meaning and purpose in life, so it can indirectly improve brain functioning through enhancing meaning and well-being. Keep up the good work!',
            moreTips:
              'Press here for some additional tips to improve Cognitive Stimulation',
            tipsTitle: 'Tips for Further Enhancing Cognitive Stimulation',
            tips: [
              {
                title: 'Learn a new skill:',
                description:
                  'This could be a new language or how to play a musical instrument. Perhaps you want to learn Mediterranean Cooking (recommended!) or chess. Any skill that challenges your brain in ways it has not been challenged before will be beneficially stimulating.',
              },
              {
                title: 'Become an “expert”:',
                description:
                  "Pick a topic that interests you that you either know nothing about or would like to know more about. This should be a relatively restricted topic that you can get a good understanding of without having to get a PhD in it. Some topics that others have chosen include: the life of Beethoven, the “lost wax” process, the effect of psychedelic drugs on mental illness, or solar flares. Spend a couple of months learning all you can about your chosen topic. Make a file for it (either a hard file or on your computer). Include photos, articles, notes. You will end up literally being an expert. You can then choose another topic a few months later. Not only will you have great fodder for conversations, you will be stimulating your brain in positive ways. For more on this exercise, see <a href='https://www.psychologytoday.com/us/blog/life-art/202003/creative-activity-and-covid-19-captivity' target='_blank'>https://www.psychologytoday.com/us/blog/life-art/202003/creative-activity-and-covid-19-captivity</a>.",
              },
              {
                title: 'Play a video game.',
                description:
                  'Video games have been shown to be addictive, so this recommendation is to play in moderation. However, for older adults, two types of video games, 3-D video games (Super Mario 3D World was used in one study) and strategy-based video games (Rise of Nations was used in one study) have been shown to increase cognitive functioning and reduce risk for (or at least delay) dementia.',
              },
            ],
          },
          'Could improve': {
            text:
              'Your score on the Cognitive Stimulation Lifestyle Factor indicates that you <strong>Could Improve</strong> in this area to bolster your brain health. Cognitive stimulation is important for keeping the brain healthy throughout the lifespan. Cognitive stimulation early in life helps build cognitive reserve. Later in life, it keeps the brain supple and improves plasticity. It is important for staving off cognitive decline. Cognitive stimulation is also associated with increased meaning and purpose in life, so it can indirectly improve brain functioning through enhancing well-being.',
            moreTips:
              'Press here for some tips to improve your Cognitive Stimulation score',
            tipsTitle: 'Tips for Enhancing Cognitive Stimulation',
            tips: [
              {
                title: 'Learn a new skill:',
                description:
                  'This could be a new language or how to play a musical instrument. Perhaps you want to learn Mediterranean Cooking (recommended!) or chess. Any skill that challenges your brain in ways it has not been challenged before will be beneficially stimulating.',
              },
              {
                title: 'Become an “expert”:',
                description:
                  "Pick a topic that interests you that you either know nothing about or would like to know more about. This should be a relatively restricted topic that you can get a good understanding of without having to get a PhD in it. Some topics that others have chosen include: the life of Beethoven, the “lost wax” process, the effect of psychedelic drugs on mental illness, or solar flares. Spend a couple of months learning all you can about your chosen topic. Make a file for it (either a hard file or on your computer). Include photos, articles, notes. You will end up literally being an expert. You can then choose another topic a few months later. Not only will you have great fodder for conversations, you will be stimulating your brain in positive ways. For more on this exercise, see <a href='https://www.psychologytoday.com/us/blog/life-art/202003/creative-activity-and-covid-19-captivity' target='_blank'>https://www.psychologytoday.com/us/blog/life-art/202003/creative-activity-and-covid-19-captivity</a>.",
              },
              {
                title: 'Play a video game.',
                description:
                  'Video games have been shown to be addictive, so this recommendation is to play in moderation. However, for older adults, two types of video games, 3-D video games (Super Mario 3D World was used in one study) and strategy-based video games (Rise of Nations was used in one study) have been shown to increase cognitive functioning and reduce risk for (or at least delay) dementia.',
              },
            ],
          },
          'Needs improvement': {
            text:
              'Your score on the Cognitive Stimulation Lifestyle Factor indicates that you <strong>Need to Improve</strong> in this area to bolster your brain health and reduce your risk of brain decline. Cognitive stimulation is important for keeping the brain healthy throughout the lifespan. Cognitive stimulation early in life helps build cognitive reserve. Later in life, it keeps the brain supple and improves plasticity. It is important for staving off cognitive decline. Cognitive stimulation is also associated with increased meaning and purpose in life, so it can indirectly improve brain functioning through enhancing well-being.',
            moreTips:
              'Press here for some tips to improve Cognitive Stimulation',
            tipsTitle: 'Tips for Enhancing Cognitive Stimulation',
            tips: [
              {
                title: 'Learn a new skill:',
                description:
                  'This could be a new language or how to play a musical instrument. Perhaps you want to learn Mediterranean Cooking (recommended!) or chess. Any skill that challenges your brain in ways it has not been challenged before will be beneficially stimulating.',
              },
              {
                title: 'Become an “expert”:',
                description:
                  "Pick a topic that interests you that you either know nothing about or would like to know more about. This should be a relatively restricted topic that you can get a good understanding of without having to get a PhD in it. Some topics that others have chosen include: the life of Beethoven, the “lost wax” process, the effect of psychedelic drugs on mental illness, or solar flares. Spend a couple of months learning all you can about your chosen topic. Make a file for it (either a hard file or on your computer). Include photos, articles, notes. You will end up literally being an expert. You can then choose another topic a few months later. Not only will you have great fodder for conversations, you will be stimulating your brain in positive ways. For more on this exercise, see <a href='https://www.psychologytoday.com/us/blog/life-art/202003/creative-activity-and-covid-19-captivity' target='_blank'>https://www.psychologytoday.com/us/blog/life-art/202003/creative-activity-and-covid-19-captivity</a>.",
              },
              {
                title: 'Play a video game.',
                description:
                  'Video games have been shown to be addictive, so this recommendation is to play in moderation. However, for older adults, two types of video games, 3-D video games (Super Mario 3D World was used in one study) and strategy-based video games (Rise of Nations was used in one study) have been shown to increase cognitive functioning and reduce risk for (or at least delay) dementia.',
              },
            ],
          },
        },
        {
          moreInfo: 'Press here to see more information on our website',
          infoLink: 'https://www.brainhealthvitals.com/brain-safe-environment',
          moreVideoInfo: 'For more information check out the video',
          videoInfoLink: 'https://youtu.be/d7y0qiw6xg8',
          'Doing well': {
            text:
              'Congratulations! Your score on the Environment and Brain Safety Lifestyle Factor indicates that you are <strong>Doing Well</strong> in this area of brain health protection! Keeping your brain physically safe is an important part of brain health. By protecting it from blows and physical injuries, as well as from as many environmental toxins as possible, you will be giving your brain the best chance to perform all of its very important functions to the best of its ability. Toxins and injuries are associated with mental illness, disabilities, brain diseases, cognitive decline, and dementia. Although we don’t yet know all the effects that the chemicals in our current environment have on our brains, avoiding those that we know are dangerous is a crucial protective factor for brain health. You are doing a good job with this',
            moreTips:
              'Press here for some tips to improve your Brain Safety and Environment score',
            tipsTitle:
              'Tips for Further Enhancing Environment and Brain Safety',
            tips: [
              {
                title:
                  'Wear protective head gear when exposing your head to situations where a fall, collision, or jarring motion is possible or likely:',
                description:
                  'These situations especially include certain sports and riding in or on certain vehicles, such as bicycles, motorcycles, skates, or scooters. The helmet should fit properly and be tested for fit on a regular basis.',
              },
              {
                title:
                  'Avoid using commercial herbicides or pesticides in your garden or yard:',
                description:
                  'Many herbicides or pesticides contain chemicals that have been shown to have detrimental effects in the brain. You can make herbicides or pesticides that are effective without the harmful chemicals. Check the internet for a variety of ideas.',
              },
              {
                title:
                  'Try to buy local, organically grown produce when possible:',
                description:
                  'Because brain-harmful chemicals like paraquat and atrazine are still legal in the U.S. and are still used by some commercial growers, it is important for brain health to avoid exposure to these chemicals as often as possible.',
              },
            ],
          },
          'Could improve': {
            text:
              'Your score on the Environment and Brain Safety Lifestyle Factor indicates that you <strong>Could Improve</strong> in this area to bolster your brain health. Keeping your brain physically safe is an important part of brain health. By protecting it from blows and physical injuries, as well as from as many environmental toxins as possible, you will be giving your brain the best chance to perform all of its very important functions to the best of its ability. Toxins and injuries are associated with mental illness, disabilities, brain diseases, cognitive decline, and dementia. Although we don’t yet know all the effects that the chemicals in our current environment have on our brains, avoiding those that we know are dangerous is a crucial protective factor for brain health. You are doing a good job with this.',
            moreTips:
              'Press here for some tips to improve your Brain Safety and Environment score',
            tipsTitle: 'Tips for Enhancing Environment and Brain Safety',
            tips: [
              {
                title:
                  'Wear protective head gear when exposing your head to situations where a fall, collision, or jarring motion is possible or likely:',
                description:
                  'These situations especially include certain sports and riding in or on certain vehicles, such as bicycles, motorcycles, skates, or scooters. The helmet should fit properly and be tested for fit on a regular basis.',
              },
              {
                title:
                  'Avoid using commercial herbicides or pesticides in your garden or yard:',
                description:
                  'Many herbicides or pesticides contain chemicals that have been shown to have detrimental effects in the brain. You can make herbicides or pesticides that are effective without the harmful chemicals. Check the internet for a variety of ideas.',
              },
              {
                title:
                  'Try to buy local, organically grown produce when possible:',
                description:
                  'Because brain-harmful chemicals like paraquat and atrazine are still legal in the U.S. and are still used by some commercial growers, it is important for brain health to avoid exposure to these chemicals as often as possible.',
              },
            ],
          },
          'Needs improvement': {
            text:
              'Your score on the Environment and Brain Safety Lifestyle Factor indicates that you <strong>Need Improvement</strong> in this area to bolster your brain health and reduce your risk of brain decline. Keeping your brain physically safe is an important part of brain health. By protecting it from blows and physical injuries, as well as from as many environmental toxins as possible, you will be giving your brain the best chance to perform all of its very important functions to the best of its ability. Toxins and injuries are associated with mental illness, disabilities, brain diseases, cognitive decline, and dementia. Although we don’t yet know all the effects that the chemicals in our current environment have on our brains, avoiding those that we know are dangerous is a crucial protective factor for brain health. You are doing a good job with this.',
            moreTips:
              'Press here for some tips to improve your Brain Safety and Environment score',
            tipsTitle: 'Tips for Enhancing Environment and Brain Safety',
            tips: [
              {
                title:
                  'Wear protective head gear when exposing your head to situations where a fall, collision, or jarring motion is possible or likely:',
                description:
                  'These situations especially include certain sports and riding in or on certain vehicles, such as bicycles, motorcycles, skates, or scooters. The helmet should fit properly and be tested for fit on a regular basis.',
              },
              {
                title:
                  'Avoid using commercial herbicides or pesticides in your garden or yard:',
                description:
                  'Many herbicides or pesticides contain chemicals that have been shown to have detrimental effects in the brain. You can make herbicides or pesticides that are effective without the harmful chemicals. Check the internet for a variety of ideas.',
              },
              {
                title:
                  'Try to buy local, organically grown produce when possible:',
                description:
                  'Because brain-harmful chemicals like paraquat and atrazine are still legal in the U.S. and are still used by some commercial growers, it is important for brain health to avoid exposure to these chemicals as often as possible.',
              },
            ],
          },
        },
      ]
    },
    icons() {
      if (this.isTheRiseFrameworkSurvey) {
        return [
          require('@/assets/screener-icons/the-rise-framework/feature-lister.png'),
          require('@/assets/screener-icons/the-rise-framework/pillar-pitcher.png'),
          require('@/assets/screener-icons/the-rise-framework/glorifier.png'),
          require('@/assets/screener-icons/the-rise-framework/overexplainer.png'),
          require('@/assets/screener-icons/the-rise-framework/day-jobber.png'),
          require('@/assets/screener-icons/the-rise-framework/tactician.png'),
          require('@/assets/screener-icons/the-rise-framework/defender.png'),
          require('@/assets/screener-icons/the-rise-framework/copy-catter.png'),
          require('@/assets/screener-icons/the-rise-framework/schmoozer.png'),
          require('@/assets/screener-icons/the-rise-framework/interrogator.png'),
        ]
      }
      return [
        require('@/assets/screener-icons/nutrition.png'),
        require('@/assets/screener-icons/general_health.png'),
        require('@/assets/screener-icons/physical_activity.png'),
        require('@/assets/screener-icons/emotional_wellbeing.png'),
        require('@/assets/screener-icons/stress_resilience.png'),
        require('@/assets/screener-icons/sleep.png'),
        require('@/assets/screener-icons/meaning_and_purpose.png'),
        require('@/assets/screener-icons/social_connection.png'),
        require('@/assets/screener-icons/cognitive_stimulation.png'),
        require('@/assets/screener-icons/home_environment.png'),
      ]
    },
    totalScore() {
      return this.results.reduce((acc, result) => acc + (result?.score || 0), 0)
    },
    totalMaxScore() {
      return this.results.reduce(
        (acc, result) => acc + (result?.maxScore || 0),
        0,
      )
    },
    totalScorePercentage() {
      return (this.totalScore / this.totalMaxScore) * 100
    },
  },
  methods: {
    stringify,
    showTips(index) {
      const newExpandedTips = [...this.expandedTips]
      newExpandedTips[index] = true
      this.expandedTips = [...newExpandedTips]
    },
    handleScroll(event) {
      if (!surveyResultIsScrolling) {
        window.requestAnimationFrame(() => {
          const offsetWidth = event?.target?.offsetWidth || 0
          const scrollWidth = event?.target?.scrollWidth || 0
          const scrollLeft = event?.target?.scrollLeft || 0

          this.surveyResultCanScrollLeft = scrollLeft > 0
          this.surveyResultCanScrollRight =
            offsetWidth + scrollLeft !== scrollWidth

          surveyResultIsScrolling = false
        })
        surveyResultIsScrolling = true
      }
    },
    startObeserveScroll() {
      this.$refs.surveyResultsChart.addEventListener(
        'scroll',
        this.handleScroll,
      )
      this.refreshScrollIndicators()
    },
    refreshScrollIndicators() {
      debounce(
        () => this.handleScroll({ target: this.$refs.surveyResultsChart }),
        1000,
        'init_survey_results',
      )
    },
    getSummaryStatus(summary) {
      if (this.isTheRiseFrameworkSurvey) {
        return 'theriseframework'
      }
      if (summary === 'Could improve') return 'normal'
      if (summary === 'Needs improvement') return 'bad'
      if (summary === 'Doing well') return 'good'
      return 'good'
    },
    print() {
      this.loadingPrint = true
      // Expand all expansion panels
      this.resultsPanels = [...Array(this.results.length)].map((k, i) => i)

      // Expand all tips texts
      const expandedTips = [...Array(this.results.length)].map(_ => true)
      this.expandedTips = [...expandedTips]

      this.$vuetify.goTo(0)

      setTimeout(() => {
        this.loadingPrint = false
        // Print
        window.print()
      }, 500)
    },
  },
  async mounted() {
    await this.$nextTick()
    this.startObeserveScroll()
    window.addEventListener('resize', this.refreshScrollIndicators)
  },
  beforeDestroy() {
    this.$refs.surveyResultsChart.removeEventListener(
      'scroll',
      this.handleScroll,
    )
    window.removeEventListener('resize', this.refreshScrollIndicators)
  },
}
</script>

<style lang="scss">
@import '@/styles/variables';

.section-summary-color {
  &--good {
    background: #1da462;
    -webkit-print-color-adjust: exact !important; /* Chrome, Safari */
    color-adjust: exact !important; /*Firefox*/
  }
  &--normal {
    background: #ffcd46;
    -webkit-print-color-adjust: exact !important; /* Chrome, Safari */
    color-adjust: exact !important; /*Firefox*/
  }
  &--bad {
    background: #dd5144;
    -webkit-print-color-adjust: exact !important; /* Chrome, Safari */
    color-adjust: exact !important; /*Firefox*/
  }
  &--theriseframework {
    background: var(--main-color);
    -webkit-print-color-adjust: exact !important; /* Chrome, Safari */
    color-adjust: exact !important; /*Firefox*/
  }

  &--text-good {
    color: #1da462;
  }
  &--text-normal {
    color: #ffcd46;
  }
  &--text-bad {
    color: #dd5144;
  }
  &--text-theriseframework {
    color: #00a987;
  }
}

.survey-results-texts {
  &__item {
    &-header {
      .v-image {
        max-width: 48px;
        margin-right: 16px;
      }
    }
    &-content {
      text-align: left;

      a {
        text-decoration: underline;
      }
    }
    &-tips {
    }
    &-compare {
      text-align: center;
      &-title {
        &--win {
          color: #1da462;
        }
        &--lose {
          color: #dd5144;
        }
      }
      margin-bottom: 24px;
    }
    &-example {
      padding-top: 24px;
      border-top: 1px solid var(--bh-gray--5);
    }
  }
}

.survey-results-chart {
  max-width: 100%;

  .book-promo {
    img {
      width: 360px;
    }

    &__btn {
      .v-btn__content {
        color: #fff;
      }
    }
  }
}

.survey-results {
  max-width: 100%;
  padding: 16px;
  background: var(--bh-white);
  -webkit-print-color-adjust: exact !important; /* Chrome, Safari */
  color-adjust: exact !important; /*Firefox*/
  border-radius: 8px;

  .allow-scroll-left,
  .allow-scroll-right {
    position: relative;

    &::before,
    &::after {
      content: ' ';
      display: block;
      bottom: 0;
      left: 0;
      pointer-events: none;
      position: absolute;
      right: 0;
      top: 0;
      transition: all 0.2s ease-out;
    }
  }

  .allow-scroll-left::before {
    content: ' ';
    box-shadow: 3em 0 2em -1em var(--bh-white) inset;
  }

  .allow-scroll-right::after {
    content: ' ';
    box-shadow: -3em 0 2em -1em var(--bh-white) inset;
  }

  &__legend {
    text-align: left;
    &-item {
      display: flex;
      align-items: center;
      margin-bottom: 0.5em;

      &-box {
        width: 1em;
        height: 1em;
        display: inline-block;
        margin-right: 8px;
      }
    }
  }
  &__chart {
    display: flex;
    justify-content: center;
    max-width: 100%;
    overflow-x: auto;
    height: 360px;
    @media (min-width: 600px) {
      height: 460px;
    }
    &-item {
      display: flex;
      flex-direction: column;
      width: 56px;
      padding: 8px;
      @media (min-width: 600px) {
        width: 64px;
        padding: 8px;
      }
      &-bar {
        flex: 1;
        display: flex;
        align-items: flex-end;
        margin-bottom: 16px;
        -webkit-print-color-adjust: exact !important; /* Chrome, Safari */
        color-adjust: exact !important; /*Firefox*/
        &-fill {
          width: 100%;
        }
      }
    }
  }
  &__total-score {
    font-weight: 600;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;

    &-progress {
      font-weight: 600;
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    &-label {
      font-size: 12px;
      color: var(--bh-gray--3);
    }
    &-value {
      color: var(--bh-blue);
      font-size: 20px;
    }
    &-max-value {
      font-size: 12px;
    }
  }
}
</style>
